import type { NavigationGuardNext, RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import {
  AUTO_ATTEMPT_KEY,
  getAccessToken,
  handleAuthCallback,
  hasValidAuthContext,
  initiateAuthorize,
  INTERNAL_REDIRECT_KEY,
  restoreSession,
} from '@/api/idp'
import { routeNames } from '@/constants/router'
import InvalidPortalError from '@/errors/InvalidPortalError'
import UnauthorizedError from '@/errors/UnauthorizedError'
import AppService from '@/services/appService'
import { useAppStore } from '@/stores/app'
import { isEmbedded } from '@/api/client'

const MAX_AUTO_ATTEMPTS = 2

export function normalizeRedirect(path?: string | null): string | null {
  if (!path || path === '/' || path === '/login') {
    return null
  }
  if (path.startsWith('/login?') || path.startsWith('/login/')) {
    return null
  }
  // Canvas/pipeline routes requiring in-memory state: redirect to /mode for that business profile
  const wizardMatch = path.match(
    /^\/app\/(\d+)\/(editor|generate|translate|icon|generate-similar|ai-image-edit)(\b|\/|\?)/i,
  )
  // A shared history link (?historyId=) opens the tool page directly; History restores it there.
  if (wizardMatch && !path.includes('historyId=')) {
    return `/app/${wizardMatch[1]}/mode`
  }
  if (/^\/(editor|generate|translate|icon|generate-similar|ai-image-edit)(\b|\/|\?)/i.test(path)) {
    return null
  }
  return path
}

export const AUTO_LOGIN_QUERY = 'auto'
export const AUTO_LOGIN_STORAGE_KEY = 'dx_login_auto'

export function loginLocation(options?: {
  redirect?: string
  auto?: boolean
}): RouteLocationRaw {
  if (options?.auto) {
    sessionStorage.setItem(AUTO_LOGIN_STORAGE_KEY, 'pending')
  }

  const query: Record<string, string> = {}
  if (options?.redirect) {
    query.redirect = options.redirect
  }
  if (options?.auto) {
    query[AUTO_LOGIN_QUERY] = '1'
  }

  return { name: routeNames.LOGIN, query }
}

export function shouldAutoSignIn(autoQuery: unknown): boolean {
  const pending = sessionStorage.getItem(AUTO_LOGIN_STORAGE_KEY) === 'pending'
  if (autoQuery === '1' && pending) {
    sessionStorage.setItem(AUTO_LOGIN_STORAGE_KEY, 'started')
    return true
  }

  sessionStorage.removeItem(AUTO_LOGIN_STORAGE_KEY)
  return false
}

export async function resolvePostLoginRoute(
  to: RouteLocationNormalized,
  options?: { redirectPath?: string | null }
): Promise<RouteLocationRaw> {
  const appStore = useAppStore()
  const appService = new AppService()

  if (!appStore.user) {
    await appStore.hydrateUserFromToken()
  }

  if (appStore.portal == null) {
    const portalDomain = import.meta.env.DEV ? 'uni.stunnr.co' : window.location.hostname
    try {
      appStore.setPortal(await appService.getPortalData(portalDomain))
    } catch (error) {
      console.error(error)
    }
  }

  if (appStore.businessProfiles.length === 0 && appStore.user && appStore.portal) {
    try {
      appStore.setBusinessProfiles(
        await appService.getPortalBusinessProfiles(appStore.user.id, appStore.portal.id),
      )
    } catch (error) {
      console.error(error)
    }
  }

  const explicitRedirect = normalizeRedirect(
    options?.redirectPath ||
    (typeof to.query.redirect === 'string' ? to.query.redirect : null) ||
    sessionStorage.getItem(INTERNAL_REDIRECT_KEY)
  )
  sessionStorage.removeItem(INTERNAL_REDIRECT_KEY)

  if (explicitRedirect) {
    sessionStorage.setItem('dx_post_login_redirect', explicitRedirect)
    return explicitRedirect
  }

  // In embed mode, never show Profile Selection. Use the selected or first profile.
  if (isEmbedded || appStore.isInEmbedMode) {
    const bpId = appStore.selectedBusinessProfileId || appStore.businessProfiles[0]?.id
    if (bpId) {
      appStore.setSelectedBusinessProfileId(bpId)
      return { name: routeNames.MODE, params: { businessProfileId: bpId } }
    }
  }

  if (appStore.businessProfiles.length === 0) {
    return { name: routeNames.PROFILE_SELECTION }
  }

  if (appStore.businessProfiles.length === 1) {
    const businessProfileId = appStore.businessProfiles[0].id
    appStore.setSelectedBusinessProfileId(businessProfileId)
    return { name: routeNames.MODE, params: { businessProfileId } }
  }

  return { name: routeNames.PROFILE_SELECTION }
}

export async function completeLoginWithBusinessProfile(
  businessProfileId: number,
  redirect?: string | null,
): Promise<RouteLocationRaw> {
  const appStore = useAppStore()
  appStore.setSelectedBusinessProfileId(businessProfileId)

  const normalizedRedirect = normalizeRedirect(redirect ?? null)
  if (normalizedRedirect) {
    sessionStorage.setItem('dx_post_login_redirect', normalizedRedirect)
    return normalizedRedirect
  }

  return { name: routeNames.MODE, params: { businessProfileId } }
}

export async function loginGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  await restoreSession()

  const accessToken = getAccessToken()
  if (accessToken) {
    const destination = await resolvePostLoginRoute(to)
    next(destination)
    return
  }

  if (isEmbedded) {
    next(false)
    return
  }

  const redirectTarget =
    normalizeRedirect(typeof to.query.redirect === 'string' ? to.query.redirect : null) ||
    normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY)) ||
    '/'
  sessionStorage.setItem(INTERNAL_REDIRECT_KEY, redirectTarget)

  next()
}

export async function processAuthCallback(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | null> {
  const error = typeof to.query.error === 'string' ? to.query.error : null

  if (error) {
    sessionStorage.removeItem(AUTO_ATTEMPT_KEY)
    const savedRedirect = normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY))
    return {
      name: routeNames.LOGIN,
      query: savedRedirect ? { redirect: savedRedirect } : {},
    }
  }

  const code = typeof to.query.code === 'string' ? to.query.code : null
  const state = typeof to.query.state === 'string' ? to.query.state : null

  if (code && state && hasValidAuthContext(state)) {
    try {
      const redirect = await handleAuthCallback(code, state)

      const appStore = useAppStore()
      await appStore.hydrateUserFromToken()
      const destination = await resolvePostLoginRoute(to, { redirectPath: redirect })
      
      sessionStorage.removeItem(AUTO_ATTEMPT_KEY)
      return destination
    } catch (callbackError: any) {
      console.error(callbackError)
      if (isEmbedded) {
        const { isTokenVersionRevoked, handleEmbedFlowError } = await import('@/helpers/embedHelper')
        if (isTokenVersionRevoked(callbackError)) {
          handleEmbedFlowError(callbackError)
          return null
        }
        throw callbackError
      }
      if (
        callbackError instanceof UnauthorizedError ||
        callbackError?.name === 'UnauthorizedError' ||
        callbackError?.name === 'ForbiddenBusinessProfileRequestedError'
      ) {
        return null
      }
      throw callbackError
    }
  }

  const attempts = parseInt(sessionStorage.getItem(AUTO_ATTEMPT_KEY) || '0', 10)
  if (attempts < MAX_AUTO_ATTEMPTS) {
    sessionStorage.setItem(AUTO_ATTEMPT_KEY, String(attempts + 1))
    const redirectTarget = normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY)) || '/'
    await initiateAuthorize(redirectTarget, 'none')
    return null
  }

  const savedRedirect = normalizeRedirect(sessionStorage.getItem(INTERNAL_REDIRECT_KEY))
  sessionStorage.removeItem(AUTO_ATTEMPT_KEY)
  return {
    name: routeNames.LOGIN,
    query: savedRedirect ? { redirect: savedRedirect } : {},
  }
}

export async function authCallbackGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const destination = await processAuthCallback(to)
  if (destination) {
    next(destination)
  } else {
    next(false)
  }
}

export function isEmbedRoute(to: RouteLocationNormalized): boolean {
  return typeof to.query.token === 'string' && to.query.token.length > 0
}

export async function globalRouterGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const appStore = useAppStore()
  const appService = new AppService()

  if (isEmbedRoute(to)) {
    next()
    return
  }

  await restoreSession()

  if (isEmbedded) {
    const { getIsSessionRevoked } = await import('@/helpers/embedHelper')
    if (getIsSessionRevoked()) {
      next(false)
      return
    }
  }

  let accessToken = getAccessToken()
  let user = appStore.user

  if (accessToken && !user) {
    try {
      user = await appStore.hydrateUserFromToken()
    } catch (e) {
      console.error('Failed to hydrate user in globalRouterGuard:', e)
    }
  }

  if (to.name === 'Forbidden' || to.name === routeNames.NOT_FOUND || to.name === 'NotFound') {
    next()
    return
  }

  if (appStore.portal == null) {
    const portalDomain = import.meta.env.DEV ? 'uni.stunnr.co' : window.location.hostname
    try {
      appStore.setPortal(await appService.getPortalData(portalDomain))
    } catch (error) {
      console.error(error)
      if (error instanceof InvalidPortalError) {
        next({ name: routeNames.NOT_FOUND })
        return
      }
    }
  }

  if (appStore.businessProfiles.length === 0 && appStore.user && appStore.portal) {
    try {
      appStore.setBusinessProfiles(
        await appService.getPortalBusinessProfiles(appStore.user.id, appStore.portal.id),
      )
    } catch (error) {
      console.error(error)
    }
  }

  if (to.meta.requiresAuth) {
    accessToken = getAccessToken()
    if (!user || !accessToken) {
      if (isEmbedded) {
        next(false)
        return
      }
      next(loginLocation({ redirect: to.fullPath, auto: true }))
      return
    }
  }

  if (to.name === routeNames.DEFAULT && !user && !accessToken) {
    if (isEmbedded) {
      next(false)
      return
    }
    next(loginLocation({ redirect: to.fullPath, auto: true }))
    return
  }

  if (to.name === routeNames.DEFAULT && user && accessToken) {
    next(await resolvePostLoginRoute(to))
    return
  }

  if (sessionStorage.getItem('dx_post_login_redirect')) {
    sessionStorage.removeItem('dx_post_login_redirect')
  }

  // Routes that require in-memory canvas/pipeline state from a prior step.
  // On cold visit or after auth transition (Callback/Login), in-memory canvas state is lost,
  // so these safely restart from Mode.
  // Independent routes like Resize, GrabEngine, Feeds, and input forms can load directly.
  // A shared history link (?historyId=) also loads directly; History restores the state there.
  const stateDependentRouteNames = [
    routeNames.EDITOR,
    routeNames.TRANSLATE,
    routeNames.BUTTON_GIF,
    routeNames.DYNAMIC_CREATIVE,
    routeNames.GENERATE,
    routeNames.AI_IMAGE_EDIT,
  ]
  const authOrColdSources = [routeNames.DEFAULT, routeNames.CALLBACK, routeNames.LOGIN]

  if (
    stateDependentRouteNames.includes(to.name as string) &&
    !to.query.historyId &&
    (!from.name ||
      authOrColdSources.includes(from.name as string) ||
      from.path === '/' ||
      from.path === '/callback' ||
      from.path === '/login')
  ) {
    const businessProfileId =
      to.params.businessProfileId ||
      appStore.selectedBusinessProfileId ||
      appStore.businessProfiles[0]?.id
    if (businessProfileId) {
      next({
        name: routeNames.MODE,
        params: { businessProfileId },
      })
      return
    }
  }

  next()
}
